import queryString from 'querystring';
import axiosInstance from './axios-config';
import { persistStateToSession } from './webstorage';
import * as MsgBarActions from './modules/system-shared/message-bar-action-creator';
import * as TYPES from './modules/auth/auth-actiontypes';
import { logout } from './modules/auth/auth-middleware';

const MAX_RETRY_TOKEN_COUNT = 300;
const RETRY_TOKEN_TIMEOUT = 100;
const LOCAL_STORAGE_KEY = 'patient:auth';

const DEFAULT_OPTIONS = {
  maxAttempts: 3,
  waitTime: 100,
  errorCodes: [],
};

const getSessionStorage = () => JSON.parse(sessionStorage.getItem(LOCAL_STORAGE_KEY));

const shouldRetry = (error) => {
  const { errorCodes } = error.config;
  const { maxAttempts, __retryCount: retryCount = 0 } = error.config;

  // We might need to umcomment the following line and comment the line after in new versions of axios.
  // const { response: { status: statusCode } = {} } = error;
  const statusCode = error.status;

  let shouldRetryForStatus = false;

  if (
    (errorCodes.length === 0 && statusCode === 401) ||
    errorCodes.includes(statusCode)
  ) {
    shouldRetryForStatus = true;
  }

  if (shouldRetryForStatus && retryCount < maxAttempts) {
    return true;
  }

  return false;
};


const addAccessTokenToRequest = (config) => {
  const storage = getSessionStorage();
  if (!storage) {
    return config;
  }

  const configWithToken = config;
  configWithToken.headers.Authorization = 'Bearer ' + storage.access_token;

  return configWithToken;
};

const isAccessTokenExpired = () => {
  const storage = getSessionStorage();
  if (!storage || !storage.expires) {
    return false;
  }

  return Date.parse(storage.expires) < Date.parse(Date());
};

const setIsTokenRefreshInProgressFlag = (isTokenRefreshInProgress) => {
  const storage = getSessionStorage();
  const state = {
    isAuthenticating: false,
    isAuthenticated: true,
    access_token: storage.access_token,
    refresh_token: storage.refresh_token,
    loginType: storage.loginType,
    isTokenRefreshInProgress,
    expires: storage.expires,
  };

  persistStateToSession(LOCAL_STORAGE_KEY, state);
};

const updateNewTokensInState = (response) => {
  const storage = getSessionStorage();
  const state = {
    isAuthenticating: false,
    isAuthenticated: true,
    access_token: response.access_token,
    refresh_token: response.refresh_token,
    loginType: storage.loginType,
    isTokenRefreshInProgress: false,
    expires: new Date((Date.now() + (response.expires_in * 1000)) - 15000),
  };

  persistStateToSession(LOCAL_STORAGE_KEY, state);

  return {
    type: TYPES.TOKEN_UPDATE_SUCCESS,
    state,
  };
};

const postRefreshToken = () => axiosInstance.post(
  '/token',
  queryString.stringify({
    grant_type: 'refresh_token',
    refresh_token: getSessionStorage().refresh_token,
  }),
  {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    validateStatus: function validateStatus(status) {
      return status < 400;
    },
  });

const updateTokenFailureAction = () =>
  ({
    type: TYPES.TOKEN_UPDATE_FAILURE,
    state: null,
  });

const refreshAccessToken = (store) => {
  setIsTokenRefreshInProgressFlag(true);
  return postRefreshToken()
    .then(
      (response) => {
        store.dispatch(updateNewTokensInState(response.data));
        return Promise.resolve();
      }
    );
};

// If another api request has already initiated a refresh token request then delay this request
// until the refresh token request has completed.
const waitForAccessTokenToRefresh = () =>
  new Promise(
    (resolve) => {
      let currentRetryCount = 0;
      const intervalId = setInterval(() => {
        currentRetryCount += 1;
        const sessionStorage = getSessionStorage();
        if (currentRetryCount > MAX_RETRY_TOKEN_COUNT ||
          !sessionStorage || sessionStorage.isTokenRefreshInProgress === false) {
          clearInterval(intervalId);
          resolve();
        }
      }, RETRY_TOKEN_TIMEOUT);
    }
  );

const isRefreshTokenRequest = (request) => (
  request && request.data && request.data.includes && request.data.includes('grant_type=refresh_token')
);

export const processTokenRefresh = (request, store) => {
  if (isRefreshTokenRequest(request)) {
    return request;
  }
  if (isAccessTokenExpired()) {
    if (getSessionStorage().isTokenRefreshInProgress === true) {
      return waitForAccessTokenToRefresh()
        .then(
          () => addAccessTokenToRequest(request)
        );
    }
    return refreshAccessToken(store)
      .then(
        () => addAccessTokenToRequest(request)
      );
  }
  return addAccessTokenToRequest(request);
};

// const isUnauthorizedResponse = error => error.status === 401;
let loggedOut = false;

const processRefreshTokenFailure = (store) => {
  store.dispatch(updateTokenFailureAction());
  store.dispatch(logout(true)); // true is to indicate that this is for a timeout
  loggedOut = true;
  store.dispatch(MsgBarActions.showErrorMessage('sessiontimeout'));
};

let refreshSubscribers = [];

const onRrefreshed = (token) => {
  refreshSubscribers.map((req) => req(token));
  refreshSubscribers = [];
};

const subscribeTokenRefresh = (req) => {
  refreshSubscribers.push(req);
};

export const processUnauthorizedError = (error, store) => {
  const originalRequest = error.config;
  if (isRefreshTokenRequest(originalRequest)) {
    if (!loggedOut) {
      processRefreshTokenFailure(store);
    }
    /* eslint-disable prefer-object-spread */
    return Promise.reject(Object.assign({}, error, { isSessionTimeout: true }));
  }
  if (originalRequest && shouldRetry(error)) {
    const sessionStorage = getSessionStorage();
    if (!sessionStorage) {
      return Promise.reject(error);
    }

    const { __retryCount: retryCount = 0 } = originalRequest;
    Object.assign(originalRequest, {
      __retryCount: retryCount + 1,
      __isRetryRequest: true,
    });
    const waitTime = originalRequest.waitTime ? originalRequest.waitTime : 0;

    if (!sessionStorage.isTokenRefreshInProgress) {
      setIsTokenRefreshInProgressFlag(true);
      refreshAccessToken(store).then((newToken) => {
        setIsTokenRefreshInProgressFlag(false);
        onRrefreshed(newToken);
      });
    }

    if (waitTime) {
      const retryOrigReq = new Promise((resolve) => {
        setTimeout(() => {
          subscribeTokenRefresh((token) => {
            // Replace the expired token and retry
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(axiosInstance.request(originalRequest));
          });
        }, waitTime);
      });
      return retryOrigReq;
    }
    return axiosInstance.request(originalRequest);
  }
  return Promise.reject(error);
};

export const configureInterceptors = (store, options = {}) => {
  const retryConfig = {
    maxAttempts: options.maxAttempts
      ? options.maxAttempts
      : DEFAULT_OPTIONS.maxAttempts,
    waitTime: options.waitTime ? options.waitTime : DEFAULT_OPTIONS.waitTime,
    errorCodes: options.errorCodes ? options.errorCodes : DEFAULT_OPTIONS.errorCodes,
  };

  axiosInstance.interceptors.request.use((config) => processTokenRefresh(Object.assign(config, retryConfig), store));
  axiosInstance.interceptors.response.use(undefined, (error) => processUnauthorizedError(error, store));
};
